import { Box, Chip, CircularProgress, Link, Pagination, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * Mobile card layout for the Kundenportal's Objekt-Tabelle - used instead of
 * the shared DataTable/DataList below the "md" breakpoint, as that list view
 * is reused by every table in the app and an object-specific layout (status
 * badge top-right, tighter spacing) would change all of them. See
 * ContractCardList.jsx, which this mirrors.
 */
export default function ObjectCardList({
  rows,
  isLoading,
  hasLoaded,
  pageIndex = 0,
  pageSize = 10,
  totalRows = 0,
  onChangePage,
  onVertragClick,
}) {
  const theme = useTheme();
  const pageCount = Math.ceil(totalRows / pageSize) || 1;

  if (!hasLoaded && isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ opacity: isLoading ? 0.6 : 1 }}>
      <Stack spacing={1}>
        {rows.map((objekt) => {
          const address = [
            [objekt.strasse, objekt.hausnr].filter(Boolean).join(" "),
            [objekt.plz, objekt.ort].filter(Boolean).join(" "),
          ]
            .filter(Boolean)
            .join(", ");

          return (
            <Box
              key={objekt.id}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: theme.spacing(1),
                p: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 0 }}>
                  {objekt.bezeichnung || address}
                </Typography>
                <Chip
                  size="small"
                  label={objekt.is_active ? "aktiv" : "archiviert"}
                  variant={
                    objekt.is_active
                      ? "contract-status_aktiv"
                      : "contract-status_archiviert"
                  }
                  sx={{ flexShrink: 0 }}
                />
              </Box>

              {objekt.sparten?.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1 }}
                >
                  {objekt.sparten.map((sparte) => (
                    <Chip key={sparte.id} size="small" label={sparte.sparte} />
                  ))}
                </Stack>
              )}

              {objekt.vertrag && (
                <Link
                  component="button"
                  variant="caption"
                  onClick={() => onVertragClick(objekt.vertrag)}
                  sx={{ mt: 1, display: "block" }}
                >
                  {objekt.vertrag.vertragsnummer}
                </Link>
              )}
            </Box>
          );
        })}

        {hasLoaded && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Einträge gefunden.
          </Typography>
        )}
      </Stack>

      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={(_, value) =>
              onChangePage?.({ pageIndex: value - 1, pageSize })
            }
            color="primary"
            siblingCount={0}
          />
        </Box>
      )}
    </Box>
  );
}
